/**
 * The one error shape the application knows. Every backend quirk — envelopes,
 * differing field names, plain-text 5xx bodies — is flattened into this by the
 * facade. No feature, hook, or component ever inspects an HTTP library error.
 */
export type AppError = {
  status: number
  /** Stable machine code from the backend, e.g. SUBSCRIPTION_LIMIT. Translated by key. */
  code?: string
  /** Human text. A fallback only — server text is not translatable. */
  message: string
  /** Per-field messages for a 422, fed into the form via `setError`. */
  fields?: Record<string, string>
  requestId?: string
}

const APP_ERROR: unique symbol = Symbol.for('app-error')

type TaggedAppError = AppError & { [APP_ERROR]: true }

export function createAppError(error: AppError): AppError {
  const tagged: TaggedAppError = { ...error, [APP_ERROR]: true }
  return tagged
}

export function isAppError(value: unknown): value is AppError {
  return typeof value === 'object' && value !== null && APP_ERROR in value
}

/**
 * Only a real network failure. Status 0 alone is not enough — a timeout, an
 * abort, and a payload that failed its schema all carry it too, and calling
 * those «нет соединения» sends the reader to check their wifi over a bug.
 */
export const isOffline = (error: AppError) => error.status === 0 && error.code === 'OFFLINE'

export const isForbidden = (error: AppError) => error.status === 403

export const isValidationError = (error: AppError) => error.status === 422
